import { RootState, INITIAL_STATE, ValidationStatus } from "../core/state-shape";
import { http } from "@/lib/http";
import { measure } from "@/lib/performance";
import { createProjectScopedStore } from "./projectScoped";

export type ReviewState = RootState["review"];

type ReviewActions = {
  setState: (newState: ReviewState) => void;
  patch: (p: Partial<ReviewState>) => void;
  reset: () => void;
  setHunkDecision: (hunkId: string, decision: 'applied' | 'skipped') => void;
  setEditedContent: (hunkId: string, content: string) => void;
  clearEditedContent: () => void;
  validateHunkContent: (proposalId: string, hunkId: string, filePath: string, content: string) => Promise<void>;
  setHunkValidationStatus: (hunkId: string, status: ValidationStatus) => void;
};

export const useReviewStore = createProjectScopedStore<ReviewState & ReviewActions>('review', (set, get) => ({
  ...INITIAL_STATE.review,
  setState: (newState) => set(newState),
  patch: (p) => set(p),
  reset: () => set({ ...INITIAL_STATE.review }),

  setHunkDecision: (hunkId, decision) => {
    const { selections } = get();
    get().patch({
      selections: {
        ...selections,
        [hunkId]: {
          decision,
          decidedAt: new Date().toISOString(),
      },
      },
    });
  },

  setEditedContent: (hunkId, content) => {
    set(state => ({
      editedContent: {
        ...(state.editedContent || {}),
        [hunkId]: content,
      }
    }));
  },
  clearEditedContent: () => set({ editedContent: {} }),
  setHunkValidationStatus: (hunkId, status) => {
    set(state => ({
      hunkValidationStatus: {
        ...state.hunkValidationStatus,
        [hunkId]: status,
      }
    }));
  },
  validateHunkContent: measure(async (proposalId, hunkId, filePath, content) => {
    try {
      get().setHunkValidationStatus(hunkId, { status: 'pending' });
      const res = await http<{ isValid: boolean, error?: any }>('/api/validators/parse-ast', {
        method: 'POST',
        body: JSON.stringify({ filePath, content }),
      });
      if (res.isValid) {
        get().setHunkValidationStatus(hunkId, { status: 'valid' });
      } else {
        get().setHunkValidationStatus(hunkId, { status: 'invalid', error: res.error });
      }
    } catch (e) {
      console.error("Validation failed", e);
      get().setHunkValidationStatus(hunkId, { status: 'invalid', error: { line: 0, column: 0, message: 'Validation request failed' } });
    }
  }, 'review.validateHunkContent'),
}));